import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ClinicInfo } from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
}

export function ChatPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      message:
        "Hi! I'm here to help answer any questions you might have about your appointment or our services. How can I assist you today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicSlug) {
        setError('No clinic slug provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const cfg = await BookingApi.getClinicConfigBySlug(clinicSlug);
        const cs = (cfg.active_theme?.theme?.color_scheme ||
          cfg.active_theme?.color_scheme) as unknown as Record<string, string>;
        const clinicData: ClinicInfo = {
          id: String(cfg.location.id),
          name: cfg.location.name,
          slug: cfg.location.slug,
          description: cfg.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '#2563eb',
            secondaryColor: cs?.secondary || '#1d4ed8',
            accentColor: cs?.main || cs?.primary || '#2563eb',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '#2563eb',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: cfg.ai_agent,
        };
        setClinic(clinicData);
      } catch (err) {
        setError('Error loading clinic information');
        console.error('Error fetching clinic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicSlug]);

  // Theme is initialized globally in App via ThemeInitializer

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      message: inputMessage.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response with delay
    setTimeout(() => {
      const responses = [
        'Yes, we have a large parking lot just a one-minute walk from the office. When you arrive, drive about 50 meters further, and you’ll see it on your right-hand side.',
        ' Since your appointment is for a cleaning, there’s no special preparation needed. Just arrive at the office on time, and we’ll take care of the rest!',
        'Yes, we are contracted with Blue Cross. If you have secondary insurance, we can add that to your profile as well.',
      ];

      const randomResponse =
        responses[Math.floor(Math.random() * responses.length)];

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        message: randomResponse,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  return (
    <div className='h-screen w-full bg-gray-50 overflow-y-auto scroll-smooth'>
      {/* Header with Logo */}
      <div className='p-6'>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='px-6 py-8 pb-20'>
        <div className='max-w-2xl mx-auto w-full'>
          {/* Chat Wrapper */}
          <div
            className='bg-white rounded-xl flex flex-col justify-end items-stretch gap-3 p-9'
            style={{
              height: '720px',
              boxShadow: '0 1px 10px 0 rgba(10, 13, 18, 0.15)',
            }}
          >
            {/* Chat Header */}
            <div className='flex-shrink-0 flex items-center gap-3 mb-4'>
              <div className='w-12 h-12 rounded-full overflow-hidden'>
                <img
                  src='/avatar.jpg'
                  alt={clinic.aiAgent?.name}
                  className='w-full h-full object-cover'
                />
              </div>
              <div>
                <h3 className='text-lg font-semibold text-gray-900'>
                  {clinic.aiAgent?.name}
                </h3>
              </div>
            </div>

            {/* Chat Messages Area */}
            <div className='flex-1 overflow-y-auto'>
              <div className='space-y-4'>
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className='flex items-start space-x-3 max-w-xs lg:max-w-md'>
                      <div
                        className={`px-4 py-3 rounded-2xl ${
                          message.isUser
                            ? 'bg-teal-600 text-white rounded-br-md'
                            : ' text-gray-900 rounded-bl-md'
                        }`}
                      >
                        <p className='text-sm leading-relaxed'>
                          {message.message}
                        </p>
                        <p
                          className={`text-xs mt-2 ${
                            message.isUser ? 'text-teal-100' : 'text-gray-500'
                          }`}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className='flex justify-start'>
                    <div className='flex items-start space-x-3 max-w-xs lg:max-w-md'>
                      <div className='flex-shrink-0 w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center'>
                        <svg
                          className='w-4 h-4 text-white'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                          />
                        </svg>
                      </div>
                      <div className='bg-gray-50 text-gray-900 rounded-2xl rounded-bl-md px-4 py-3'>
                        <div className='flex space-x-1'>
                          <div className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'></div>
                          <div
                            className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'
                            style={{ animationDelay: '0.1s' }}
                          ></div>
                          <div
                            className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'
                            style={{ animationDelay: '0.2s' }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message Input Area */}
            <div className='flex-shrink-0'>
              <div className='relative'>
                <textarea
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder='Ask anything'
                  className='w-full px-4 py-3 pr-24 border border-gray-300 rounded-full focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none min-h-[48px] max-h-32'
                  rows={1}
                />
                <div className='absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2'>
                  {/* Send Button */}
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isTyping}
                    className={`w-8 h-8 rounded-full transition-all duration-200 flex items-center justify-center ${
                      inputMessage.trim() && !isTyping
                        ? 'bg-gray-800 text-white hover:bg-gray-800'
                        : 'bg-gray-900 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='12'
                      height='14'
                      viewBox='0 0 12 14'
                      fill='none'
                    >
                      <path
                        d='M1.18283 4.85704V9.14275C1.18283 9.31418 1.12288 9.40023 1.02002 9.48594C0.917162 9.65737 0.788641 9.6567 0.642927 9.6567C0.497213 9.6567 0.368691 9.65737 0.265834 9.48594C0.162977 9.40023 0.103027 9.31418 0.103027 9.14275V4.85704C0.103027 4.68561 0.162977 4.59956 0.265834 4.51385C0.368691 4.34242 0.497213 4.34309 0.642927 4.34309C0.788641 4.34309 0.917162 4.34242 1.02002 4.51385C1.12288 4.59956 1.18283 4.68561 1.18283 4.85704ZM3.32568 0.0573731C3.17997 0.0573731 3.04308 0.0567034 2.94022 0.228132C2.84593 0.313846 2.78578 0.399895 2.78578 0.571324V13.4285C2.78578 13.5999 2.84593 13.6859 2.94022 13.7717C3.04308 13.9431 3.17997 13.9424 3.32568 13.9424C3.46283 13.9424 3.59992 13.9431 3.70278 13.7717C3.79706 13.6859 3.85721 13.5999 3.85721 13.4285V0.571324C3.85721 0.399895 3.79706 0.313846 3.70278 0.228132C3.59992 0.0567034 3.46283 0.0573731 3.32568 0.0573731ZM6.00007 2.20023C5.85436 2.20023 5.72583 2.19956 5.62298 2.37099C5.52012 2.4567 5.46854 2.54275 5.46854 2.71418V11.2856C5.46854 11.457 5.52012 11.5431 5.62298 11.6288C5.72583 11.8002 5.85436 11.7996 6.00007 11.7996C6.14578 11.7996 6.27431 11.8002 6.37716 11.6288C6.48002 11.5431 6.5316 11.457 6.5316 11.2856V2.71418C6.5316 2.54275 6.48002 2.4567 6.37716 2.37099C6.27431 2.19956 6.14578 2.20023 6.00007 2.20023ZM8.67446 4.34309C8.53731 4.34309 8.40022 4.34242 8.29736 4.51385C8.20308 4.59956 8.14293 4.68561 8.14293 4.85704V9.14275C8.14293 9.31418 8.20308 9.40023 8.29736 9.48594C8.40022 9.65737 8.53731 9.6567 8.67446 9.6567C8.82017 9.6567 8.95706 9.65737 9.05992 9.48594C9.15421 9.40023 9.21436 9.31418 9.21436 9.14275V4.85704C9.21436 4.68561 9.15421 4.59956 9.05992 4.51385C8.95706 4.34242 8.82017 4.34309 8.67446 4.34309ZM11.3572 3.22813C11.2115 3.22813 11.083 3.31401 10.9801 3.39973C10.8773 3.48544 10.8173 3.65721 10.8173 3.74292V10.2569C10.8173 10.3426 10.8773 10.5143 10.9801 10.6001C11.083 10.6858 11.2115 10.7717 11.3572 10.7717C11.5029 10.7717 11.6314 10.6858 11.7343 10.6001C11.8372 10.5143 11.8971 10.3426 11.8971 10.2569V3.74292C11.8971 3.65721 11.8372 3.48544 11.7343 3.39973C11.6314 3.31401 11.5029 3.22813 11.3572 3.22813Z'
                        fill='white'
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer useMotion={false} />
    </div>
  );
}
